import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { IntelligencePage } from '../features/admin/IntelligencePage'
import { AuthPage, ChangePasswordPage } from '../features/auth/AuthPage'
import { CertificatePage } from '../features/certificate/CertificatePage'
import { DashboardPage } from '../features/dashboard/DashboardPage'
import { DocumentsPage } from '../features/documents/DocumentsPage'
import { ChatPage } from '../features/chat/ChatPage'
import { ArchivedChatPage, HistoryPage } from '../features/chat/HistoryPage'
import { LedgerPage } from '../features/ledger/LedgerPage'
import { ImpersonationBar } from '../features/platform/ImpersonationBar'
import { PlanningPage } from '../features/planning/PlanningPage'
import { PlatformPage } from '../features/platform/PlatformPage'
import { ProfilePage } from '../features/profile/ProfilePage'
import { ReportsPage } from '../features/reports/ReportsPage'
import { ReviewPage, ReviewQueuePage } from '../features/review/ReviewPages'
import { RoadmapPage } from '../features/roadmap/RoadmapPage'
import { VolunteersPage } from '../features/volunteers/VolunteersPage'
import { AdvicePage } from '../features/platform/AdvicePage'
import { api } from '../lib/api'
import { AfraMark } from '../design/AfraMark'

export function App() {
  const [menuOpen, setMenuOpen] = useState(false)
  const sidebarRef = useRef<HTMLElement>(null)
  useEffect(() => {
    if (!menuOpen) return
    const previous = document.activeElement as HTMLElement | null
    const controls = () => Array.from(sidebarRef.current?.querySelectorAll<HTMLElement>('a[href], button') ?? [])
      .filter((element) => element.getClientRects().length > 0)
    controls()[0]?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); setMenuOpen(false) }
      if (event.key !== 'Tab') return
      const elements = controls(), first = elements[0], last = elements.at(-1)
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
    }
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('keydown', onKey); previous?.focus() }
  }, [menuOpen])
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  useEffect(() => {
    // Pages share one scrolling workspace. A new route starts at its header,
    // rather than inheriting the previous page's scroll position on mobile.
    document.getElementById('main-content')?.scrollTo({ top: 0, left: 0 })
  }, [location.pathname])

  const { data, isLoading, isError } = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    retry: false,
  })

  if (isLoading) {
    return (
      <div className="container center" style={{ paddingTop: 'var(--space-7)' }}>
        <p className="muted">در حال بارگذاری…</p>
      </div>
    )
  }

  const user = isError ? null : data?.user

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<AuthPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  const signOut = async () => {
    await api.logout()
    queryClient.clear()
    navigate('/login')
  }

  if (user.mustChangePassword) return <ChangePasswordPage onSignOut={signOut} />

  /* Both staff roles reach the Afra-side surfaces; what a `super_admin` may
     additionally *do* there is decided inside the page, and enforced on the
     server regardless. */
  const flush = location.pathname === '/journey'
  const isStaff = user.role === 'afra_admin' || user.role === 'super_admin'
  /* Assessors and the administrators who may stand in for one. */
  const isReviewer = isStaff || user.role === 'assessor'

  return (
    /* The live chat fills the screen under the masthead; every other page scrolls as a document. */
    <div className={`app ${flush ? 'app--flush' : ''}`}>
      <ImpersonationBar user={user} />
      <a href="#main-content" className="skip-link">رفتن به محتوای صفحه</a>
      <div className="app-shell">
        <aside ref={sidebarRef} className={`sidebar ${menuOpen ? 'sidebar--open' : ''}`} id="app-navigation" aria-label="فهرست اصلی">
          <div className="sidebar__brand">
            <div className="sidebar__wordmark"><AfraMark /><strong>افرا</strong></div>
            <div className="sidebar__tagline"><b>مسیر توسعه مؤسسه</b><span>هوشمندی و توسعه سازمانی</span></div>
            <button className="btn btn--quiet btn--sm sidebar__close" onClick={() => setMenuOpen(false)} aria-label="بستن فهرست">×</button>
          </div>
          <div className="sidebar__organization">
            <span className="meta">{user.personalSpace ? 'فضای شخصی شما' : 'مؤسسه شما'}</span>
            <strong>{user.org.name}</strong>
          </div>
          <nav onClick={() => setMenuOpen(false)}>
            <NavGroup title="دستیار و ابزارها" items={[
              ['/journey', 'گفت‌وگو با افرا', 'chat'],
              ['/journey/history', 'گفت‌وگوهای قبلی', 'history'],
              ['/dashboard', 'داشبورد', 'chart'],
              ['/roadmap', 'مسیر توسعه', 'map'],
              ['/planning', 'برنامه‌ریزی', 'plan'],
              ['/ledger', 'بلوغ سازمانی', 'chart'],
              ['/documents', 'اسناد', 'file'],
              ['/reports', 'گزارش‌ها', 'file'],
              ...(user.role === 'member' ? [['/volunteers', 'داوطلبان', 'people']] as NavItem[] : []),
            ]} />
            {user.role !== 'member' && <NavGroup title={user.personalSpace ? 'مدیریت فضای شخصی' : 'مدیریت مؤسسه'} items={[
              ['/profile', 'شناسنامه مؤسسه', 'building'],
              ['/volunteers', 'داوطلبان و توانایی‌ها', 'people'],
              ['/certificate', 'گواهی و ارزیابی', 'award'],
            ]} />}
            {user.role === 'member' && <NavGroup title="اطلاعات مؤسسه" items={[
              ['/profile', 'شناسنامه مؤسسه', 'building'],
              ['/certificate', 'گواهی', 'award'],
            ]} />}
            {isReviewer && <NavGroup title="ارزیابی مؤسسه‌ها" items={[
              ['/review', 'پرونده‌های ارزیابی', 'award'],
            ]} />}
            {isStaff && <NavGroup title="مدیریت افرا" items={[
              ['/platform', 'مدیریت سامانه', 'settings'],
              ['/admin/advice', 'موقعیت و راهنمایی', 'book'],
              ['/admin', 'هوشمندی شبکه', 'chart'],
            ]} />}
          </nav>
          <div className="sidebar__account">
            <span>{user.displayName || user.email}</span>
            <button type="button" className="btn btn--ghost btn--sm" onClick={signOut}>خروج از حساب</button>
          </div>
        </aside>
        {menuOpen && <button className="sidebar-scrim" onClick={() => setMenuOpen(false)} aria-label="بستن فهرست" />}
        <div className="workspace">
          <header className="workspace__header">
            <button type="button" className="btn btn--ghost btn--sm menu-toggle" aria-label="باز کردن فهرست" aria-expanded={menuOpen} aria-controls="app-navigation" onClick={() => setMenuOpen(!menuOpen)}>☰</button>
            <strong className="workspace__title">{pageTitle(location.pathname)}</strong>
            <span className="workspace__divider" aria-hidden="true">/</span>
            <span className="meta workspace__context">{user.personalSpace ? 'فضای شخصی شما' : user.org.name}</span>
            <div className="workspace__account"><span className="meta">{user.displayName || user.email}</span><span className="workspace__avatar" aria-hidden="true">{(user.displayName || user.email).trim().charAt(0)}</span></div>
          </header>
          <main id="main-content" className={flush ? 'main--flush' : undefined}>
        <Routes>
          <Route path="/journey" element={<ChatPage user={user} />} />
          <Route path="/journey/history" element={<HistoryPage />} />
          <Route path="/journey/history/:id" element={<ArchivedChatPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/dashboard/progress" element={<DashboardPage />} />
          <Route path="/roadmap" element={<RoadmapPage />} />
          <Route path="/planning" element={<PlanningPage />} />
          <Route path="/ledger" element={<LedgerPage />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/certificate" element={<CertificatePage />} />
          <Route path="/reports" element={<ReportsPage user={user} />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/volunteers" element={<VolunteersPage canManage={user.role !== 'member'} />} />
          {isStaff && <Route path="/admin/advice" element={<AdvicePage />} /> }
          {isReviewer && <Route path="/review" element={<ReviewQueuePage />} />}
          {isReviewer && <Route path="/review/:id" element={<ReviewPage />} />}
          {isStaff && <Route path="/admin" element={<IntelligencePage />} />}
          {isStaff && <Route path="/platform" element={<PlatformPage user={user} />} />}
          {/* Staff who are not acting as a charity land on the admin surface;
              the journey would otherwise open on Afra's own empty ledger. */}
          <Route
            path="*"
            element={
              <Navigate
                to={user.impersonating ? '/journey' : isStaff ? '/platform' : isReviewer ? '/review' : '/journey'}
                replace
              />
            }
          />
        </Routes>
          </main>
        </div>
      </div>
    </div>
  )
}


type NavItem = [path: string, label: string, icon: string]

/** Header context follows real routes; prototype-only pages are not navigation. */
function pageTitle(pathname: string): string {
  const pages: [string, string][] = [
    ['/journey/history', 'گفت‌وگوهای قبلی'], ['/journey', 'گفت‌وگو با افرا'],
    ['/dashboard/progress', 'پایش پیشرفت'], ['/dashboard', 'داشبورد مؤسسه'],
    ['/roadmap', 'مسیر توسعه'], ['/planning', 'برنامه‌ریزی'], ['/ledger', 'بلوغ سازمانی'],
    ['/documents', 'اسناد'], ['/certificate', 'گواهی و ارزیابی'], ['/reports', 'گزارش‌ها'],
    ['/profile', 'شناسنامه مؤسسه'], ['/volunteers', 'داوطلبان و توانایی‌ها'],
    ['/admin/advice', 'موقعیت و راهنمایی'], ['/admin', 'هوشمندی شبکه'],
    ['/platform', 'مدیریت سامانه'], ['/review', 'پرونده‌های ارزیابی'],
  ]
  return pages.find(([path]) => pathname === path || pathname.startsWith(`${path}/`))?.[1] ?? 'افرا'
}

function NavGroup({ title, items }: { title: string; items: NavItem[] }) {
  return <section className="sidebar__group">
    <h2>{title}</h2>
    {items.map(([path, label, icon]) => <NavLink key={path} to={path}
      end={path === '/journey' || path === '/admin'}
      className={({ isActive }) => `sidebar__link${isActive ? ' is-active' : ''}`}>
      <span className={`nav-icon-tile nav-icon-tile--${icon}`}><NavIcon name={icon} /></span><span>{label}</span>
    </NavLink>)}
  </section>
}

function NavIcon({ name }: { name: string }) {
  const paths: Record<string, string> = {
    chat: 'M5 4h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9l-6 4V6a2 2 0 0 1 2-2Z M7 9h10 M7 13h6',
    history: 'M3 11a9 9 0 1 1 3 8 M3 4v7h7 M12 7v5l3 2',
    chart: 'M4 3v17h17 M8 16v-5 M13 16V6 M18 16V9',
    map: 'm3 5 6-2 6 2 6-2v16l-6 2-6-2-6 2Z M9 3v16 M15 5v16',
    plan: 'M5 3h14v18H5Z M8 8h8 M8 12h8 M8 16h5',
    file: 'M5 3h9l5 5v13H5Z M14 3v6h5 M8 13h8 M8 17h6',
    people: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2 M16 3a4 4 0 0 1 0 8 M22 21v-2a4 4 0 0 0-3-4 M13 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z',
    building: 'M4 21V3h16v18 M8 7h2 M14 7h2 M8 11h2 M14 11h2 M10 21v-6h4v6',
    award: 'M16 9a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z M7 13l-2 8 7-3 7 3-2-8',
    settings: 'M4 6h16 M4 12h16 M4 18h16 M8 3v6 M16 9v6 M10 15v6',
    book: 'M12 5C8 3 5 3 2 4v16c4-1 7-1 10 1 3-2 6-2 10-1V4c-3-1-6-1-10 1Z M12 5v16',
  }
  return <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={paths[name] || paths.file} /></svg>
}
